import { useParams, Link } from 'react-router-dom';
import {
  FileText,
  Image,
  Eye,
  ShieldCheck,
  Users,
  CreditCard,
  ExternalLink,
  ChevronRight,
  MapPin,
  Bed,
  Store,
  Warehouse,
  BookOpen,
  CalendarDays,
  Languages,
} from 'lucide-react';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { getProducerNavVisibility } from '@/config/nav.producer';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

/**
 * "Mer" page — system settings only.
 * Content editing is handled via bottom nav tabs (Översikt, Plats, etc.).
 */

interface MenuItemProps {
  label: string;
  description?: string;
  icon: React.ElementType;
  href: string;
  badge?: string;
  badgeVariant?: 'default' | 'destructive' | 'secondary' | 'outline';
  external?: boolean;
}

function MenuItem({ label, description, icon: Icon, href, badge, badgeVariant, external }: MenuItemProps) {
  const content = (
    <div className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-muted/50 transition-colors group">
      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted/60 shrink-0">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {badge && (
            <Badge variant={badgeVariant ?? 'secondary'} className="text-[10px] px-1.5 py-0">
              {badge}
            </Badge>
          )}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      {external ? (
        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
      )}
    </div>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return <Link to={href}>{content}</Link>;
}

export default function ProducerMerPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer } = useProducerById(farmId);
  const { meta } = useProducerCapabilities(farmId);

  const { offerings: producerOfferingKeys } = useProducerOfferings(farmId);
  const vis = getProducerNavVisibility(producerOfferingKeys);

  const base = `/app/producer/farm/${farmId}`;
  const isPublic = meta?.isPublic === true;
  const publicSlug = producer?.slug;
  const showVerification = meta && meta.verificationStatus !== 'verified';

  // Determine which capability is shown in bottom nav (same priority as UnifiedAppLayout)
  const dynamicSlotKey = vis.showAccommodation
    ? 'accommodation'
    : vis.showShop
      ? 'shop'
      : vis.showFleaMarket
        ? 'flea_market'
        : null;

  // Build overflow nav items — capabilities NOT shown in bottom nav
  const overflowNavItems: MenuItemProps[] = [];

  // Sida — ALWAYS in Mer (never in bottom nav)
  overflowNavItems.push({
    label: 'Sida',
    description: 'Er plats och karta',
    icon: MapPin,
    href: `${base}/plats`,
  });

  // Bokningar — always in Mer
  overflowNavItems.push({
    label: 'Bokningar',
    description: 'Alla bokningstyper',
    icon: BookOpen,
    href: `${base}/bookings`,
  });

  // Capabilities not shown in bottom nav
  if (vis.showAccommodation && dynamicSlotKey !== 'accommodation') {
    overflowNavItems.push({ label: 'Boende', icon: Bed, href: `${base}/accommodation` });
  }
  if (vis.showShop && dynamicSlotKey !== 'shop') {
    overflowNavItems.push({ label: 'Gårdsbutik', icon: Store, href: `${base}/shop` });
  }
  if (vis.showFleaMarket && dynamicSlotKey !== 'flea_market') {
    overflowNavItems.push({ label: 'Loppis', icon: Warehouse, href: `${base}/loppis` });
  }
  if (vis.showActivities) {
    // Aktiviteter is always in bottom nav, but include here for completeness on desktop Mer
    // Skip — it's a core bottom nav item
  }

  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name || 'Din gård'} title="Mer">
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-foreground">Mer</h1>
        <p className="text-sm text-muted-foreground">
          Navigering, inställningar och kontoinformation.
        </p>
      </div>

      {/* Navigation section — items not in bottom nav */}
      {overflowNavItems.length > 0 && (
        <div className="mt-6 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-4 mb-2">
            Navigering
          </p>
          {overflowNavItems.map((item) => (
            <MenuItem key={item.href} {...item} />
          ))}
        </div>
      )}

      <Separator className="my-4" />

      {/* Redigera section */}
      <div className="mt-6 space-y-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-4 mb-2">
          Redigera
        </p>
        <MenuItem
          label="Er berättelse"
          description="Namn, beskrivning och omslag"
          icon={FileText}
          href={`${base}/grundinfo`}
        />
        <MenuItem
          label="Bilder & känsla"
          description="Galleri och bildhantering"
          icon={Image}
          href={`${base}/bilder`}
        />
        <MenuItem
          label="Synlighet"
          description="Publicering och koppling till plats"
          icon={Eye}
          href={`${base}/publicering`}
        />
        <MenuItem
          label="Översättningar"
          description="Engelska texter för internationella besökare"
          icon={Languages}
          href={`${base}/oversattningar`}
        />
      </div>

      <Separator className="my-4" />

      {/* System settings */}
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground px-4 mb-2">
          System
        </p>
        {showVerification && (
          <MenuItem
            label="Verifiering & juridik"
            description="Krävs för försäljning"
            icon={ShieldCheck}
            href={`${base}/verifiering`}
            badge="Kommer snart"
            badgeVariant="outline"
          />
        )}
        <MenuItem
          label="Värdteam"
          description="Medlemmar och behörigheter"
          icon={Users}
          href={`${base}/team`}
        />
        <MenuItem
          label="Abonnemang"
          description="Plan och fakturering"
          icon={CreditCard}
          href={`${base}/billing`}
        />
      </div>

      {/* Public page link */}
      {isPublic && publicSlug && (
        <>
          <Separator className="my-4" />
          <MenuItem
            label="Visa publik sida"
            icon={ExternalLink}
            href={`/producer/${publicSlug}`}
            external
          />
        </>
      )}
    </ProducerLayout>
  );
}
